import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';

import { getClientDateTimeContext, type ClientDateTimeContext } from './client-datetime';
import { parseSseStream } from './parse-sse';
import type { ChatSseEvent, ContextSet, Conversation, LlmModel, Message } from './types';
import type { ChatPromptPreview } from './turn-debug';

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeaders(),
      ...(await getNativeBearerAuthHeaders()),
      ...init?.headers,
    },
  });
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchChatModels(): Promise<LlmModel[]> {
  const res = await apiFetch('/chat/models');
  const data = await parseJson<{ models: LlmModel[] }>(res);
  return data.models;
}

export async function listConversations(): Promise<Conversation[]> {
  const res = await apiFetch('/chat/conversations');
  const data = await parseJson<{ conversations: Conversation[] }>(res);
  return data.conversations;
}

export async function getConversation(id: string): Promise<{
  conversation: Conversation;
  messages: Message[];
}> {
  const res = await apiFetch(`/chat/conversations/${id}`);
  return parseJson(res);
}

export async function getConversationDebugPrompt(
  id: string,
  previewText?: string,
  dateTime?: ClientDateTimeContext,
): Promise<ChatPromptPreview> {
  const params = new URLSearchParams();
  if (previewText) {
    params.set('text', previewText);
  }
  const context = dateTime ?? getClientDateTimeContext();
  params.set('clientLocalTime', context.clientLocalTime);
  params.set('timezone', context.timezone);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const res = await apiFetch(`/chat/conversations/${id}/debug-prompt${query}`);
  return parseJson<ChatPromptPreview>(res);
}

export async function createConversation(input?: {
  title?: string;
  model?: string;
  contextSet?: ContextSet;
  instructions?: string;
}): Promise<Conversation> {
  const res = await apiFetch('/chat/conversations', {
    method: 'POST',
    body: JSON.stringify(input ?? {}),
  });
  const data = await parseJson<{ conversation: Conversation }>(res);
  return data.conversation;
}

export async function patchConversation(
  id: string,
  patch: {
    title?: string | null;
    model?: string;
    contextSet?: ContextSet;
    instructions?: string | null;
  },
): Promise<Conversation> {
  const res = await apiFetch(`/chat/conversations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  const data = await parseJson<{ conversation: Conversation }>(res);
  return data.conversation;
}

export async function forkConversation(id: string): Promise<Conversation> {
  const res = await apiFetch(`/chat/conversations/${id}/fork`, { method: 'POST' });
  const data = await parseJson<{ conversation: Conversation }>(res);
  return data.conversation;
}

export async function deleteConversation(id: string): Promise<void> {
  const res = await apiFetch(`/chat/conversations/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
}

export async function sendMessageStream(
  conversationId: string,
  text: string,
  onEvent: (event: ChatSseEvent) => void,
  signal?: AbortSignal,
  dateTime?: ClientDateTimeContext,
): Promise<void> {
  const context = dateTime ?? getClientDateTimeContext();
  const res = await apiFetch(`/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: JSON.stringify({
      text,
      clientLocalTime: context.clientLocalTime,
      timezone: context.timezone,
    }),
    signal,
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `Request failed (${res.status})`);
  }

  if (!res.body) {
    throw new Error('No response body');
  }

  for await (const event of parseSseStream(res.body, signal)) {
    onEvent(event);
  }
}
